/**
 * Feature switches for behaviour that has to be turned on/off without touching
 * the components that consume it. Keep each flag documented with what it gates
 * and what is needed to turn it on.
 */

/**
 * Solidgate (`SLG`) as a checkout payment method — replaces the old
 * `NEXT_PUBLIC_SOLIDGATE_ENABLE` env var, which is no longer read anywhere in
 * the code.
 *
 * This list is the ONLY switch: router locale codes, not paths (`/es` is `'es'`,
 * the root `/` is `'en'`). In these locales Solidgate is offered to every
 * account — regular, PRO and Service Box — whenever the backend lists `SLG` in
 * `allowed_payment_methods`. Everywhere else `SLG` falls back to the Stripe
 * credit-card option (`STP`). An empty list turns Solidgate off for everybody.
 * The single guard lives in `checkout/payment/payment-method-list.tsx`.
 *
 * Being a code constant, it is the same in every environment — the env var it
 * replaced allowed staging `true` with production `false`.
 */
export const SOLIDGATE_ENABLED_LOCALES: Array<string> = ['es']; //ARRAY VAZIO SE NÃO QUISERMOS O MÉTODO DE PAGAMENTO

export const isSolidgateEnabledFor = (locale?: string | null): boolean =>
  SOLIDGATE_ENABLED_LOCALES.includes(locale || 'en');

/**
 * How `wrapFetch` (`services/utils.ts`) turns a failed API response into a
 * `CustomError`.
 *
 * `true` — **single read**. The body is read once with `text()` and reused for the log,
 * for the Sentry summary and for the parse. Responses with `status >= 500` are NOT
 * parsed as JSON at all: nothing above 500 carries a payload the app reads (the pages
 * branch on 301/404/401/403 and re-throw the rest) and the body is normally HTML from
 * the gateway or Django's debug page. The real status is preserved everywhere.
 *
 * `false` — **legacy**, the exact previous behaviour, kept verbatim: `text()` followed
 * by `json()` on the same response for a 500 (which throws
 * `TypeError: Body is unusable` and replaces the API's real error with that message),
 * `status` forced to 500 whenever the body is not JSON, and the `endpoint` / `content` /
 * `useragent` accepted by the `CustomError` constructor and then discarded.
 *
 * Flipping to `false` also restores the constructor's old `!== 500` test, so a
 * 502/503/504 goes back to the `data` branch. Expect the Sentry issue
 * `Body is unusable` to come back with it — that issue IS this flag being off.
 *
 * Event volume does not grow with `true`: the explicit `captureException` stays gated
 * on `status === 500`, and 404/301 responses that arrive with an HTML body stop being
 * re-thrown as 500s, so they no longer reach Sentry at all.
 */
// Typed as `boolean` on purpose: without it TypeScript narrows the constant to the
// literal `true` and treats the legacy branch as dead code.
export const IS_API_ERROR_SINGLE_READ_ENABLED: boolean = true;

/**
 * O interruptor geral das devoluções e dos cancelamentos na Área Privada.
 *
 * **Desligada, o cliente não cria nem vê nada**: cai a secção da página da
 * encomenda (a conta dos processos e o convite a devolver), cai a saída para
 * cancelar peças, cai a entrada "Devoluções" do menu, e as quatro rotas
 * (devolução, cancelamento, listagem e detalhe de reclamações) deixam de
 * resolver — quem lá chegar por link guardado aterra na Área Privada, como já
 * acontece a qualquer página que o menu não autorize.
 *
 * Não substitui o `SHOP_RETURNS_ENABLED` do backend, que é quem responde `404`
 * aos endpoints e manda `order.returns` a `null`. Existe **por precaução**:
 * para se poder fechar a funcionalidade do lado do cliente num deploy, sem
 * esperar por um do servidor.
 *
 * Um sítio só, de propósito: espalhada por condições nos ecrãs, desligá-la
 * deixaria sempre um caminho aberto.
 */
// Tipada como `boolean` de propósito: sem isso o TypeScript reduz a constante
// ao literal `true` e trata os ramos desligados como código morto.
export const IS_RETURNS_ENABLED: boolean = true;

/**
 * ⚠️ **TEMPORÁRIO — TIRAR ANTES DE IR PARA PRODUÇÃO.** ⚠️
 *
 * Login por email e palavra-passe no Service Box fora do local.
 *
 * No Service Box a entrada é por **token** (`/service-box/lg`): o utilizador
 * chega autenticado de fora e o `/login` não é caminho público nenhum — quem lá
 * aterrar anónimo vai para `/sb/error`. A única exceção sempre foi o
 * `localhost`, para se poder trabalhar sem um token à mão.
 *
 * Esta flag estende essa exceção a **todos os ambientes que não são produção**,
 * para a equipa poder entrar no SB de dev com email e passe em vez de andar a
 * arranjar tokens.
 *
 * **Porque é que tem de sair:** o token é a autenticação do SB, e um formulário
 * de email e passe a funcionar num ambiente alcançável é uma segunda porta que
 * não devia existir. Produção já está protegida pelo `TARGET` — o valor é
 * `false` lá —, mas a porta continua escrita no código, e código que existe
 * acaba por ser ligado.
 *
 * Quando sair: apagar esta constante e, no `providers/sb-auth-guard.tsx`,
 * devolver o `isEmailLoginAllowed` ao `isRunningLocalTests` que ele era.
 */
export const IS_SB_EMAIL_LOGIN_ENABLED: boolean =
  process.env.TARGET !== 'PRODUCTION';

/**
 * Abertura gradual por idioma — o segundo interruptor, **por baixo** do de
 * cima.
 *
 * O `IS_RETURNS_ENABLED` é tudo ou nada; este diz **onde**. Só os idiomas desta
 * lista veem as devoluções; nos outros a funcionalidade comporta-se exatamente
 * como se a flag geral estivesse desligada.
 *
 * **Vale em todos os ambientes, e é de propósito.** Houve uma exceção que abria
 * tudo fora de produção, para se poder experimentar um mercado antes de o
 * abrir; tirou-se porque escondia precisamente o que há para testar — em local
 * e em staging as devoluções apareciam em dez idiomas, e o único sítio onde se
 * via o comportamento a sério era produção. Testar uma coisa num ambiente onde
 * ela se comporta de outra maneira não é testá-la.
 *
 * Os valores são os códigos do router, não os caminhos: o `/pt` é `'pt'` e a
 * raiz `/` é `'en'`, que é o idioma por omissão e não leva prefixo.
 *
 * **Abrir um mercado é acrescentar uma linha aqui** — e passa a estar aberto em
 * todo o lado, produção incluída, no deploy seguinte. Para o experimentar sem
 * isso, acrescenta-se na mesma e tira-se antes de seguir para produção.
 */
export const RETURNS_ENABLED_LOCALES: Array<string> = ['pt'];

/**
 * As devoluções estão abertas **neste idioma**?
 *
 * É esta que os ecrãs perguntam, e não a flag: é a conjunção das duas, e
 * perguntar só a de cima deixava o idioma fechado a descoberto.
 *
 * Sem idioma responde como o idioma por omissão responderia — quem não o sabe
 * dizer não é razão para abrir o que está fechado.
 */
export const areReturnsEnabledFor = (locale?: string | null): boolean => {
  if (!IS_RETURNS_ENABLED) return false;

  return RETURNS_ENABLED_LOCALES.includes(locale || 'en');
};


/**
 * Preço **por peça** no resumo "Peças a devolver" — o de compra
 * (`products[].pricing.price_final_printable` do detalhe da encomenda) e o de
 * recolha sem IVA (`bparts.per_product[].net_charged_printable` do
 * `return-cost`).
 *
 * Está `false`: a coluna não aparece e a cotação volta a correr só enquanto se
 * monta o pedido.
 *
 * **Se isto não seguir para produção, apagar tudo o que a flag toca** — está
 * marcado com `[preços por peça]` em cada sítio:
 *
 * - `modules/returns/parts-summary.tsx` — props `perProduct` e `purchasePrices`,
 *   `pickupPriceByProduct`, `hasPrices`, a terceira coluna e a nota do IVA;
 *   com elas sai também o `useIsProOrServiceBox` daí (o hook em si fica, é
 *   genérico).
 * - `modules/returns/collection-step.tsx` e `modules/returns/summary-step.tsx` —
 *   as mesmas duas props, só de passagem.
 * - `pages/private-area/returns/index.tsx` — o `purchasePrices`, o `costProductIds`
 *   a cair para as peças do pedido, o `costAddressId` e a folga do
 *   `isReturnSettled` na query da cotação.
 * - `locales/*​/private-area.json` — `returns.collection.purchase-price`,
 *   `purchase-price-net`, `pickup-price-net` e `vat-notice`.
 */
// Tipada como `boolean` de propósito: sem isso o TypeScript reduz a constante ao
// literal `false` e trata os ramos guardados como código morto.
export const IS_RETURN_PART_PRICES_ENABLED: boolean = false;

/**
 * A declaração do estado das peças, antes de avançar do passo 2 para o passo 3
 * do fluxo de devolução.
 *
 * **Está ligada só para testes.** O texto é provisório e ainda não passou pelo
 * jurídico; enquanto for assim, é isto que a tira de produção — pôr a `false`
 * antes de seguir.
 *
 * Se não seguir, apagar o que a flag toca — está marcado com `[declaração]`:
 *
 * - `modules/returns/terms-check.tsx` — o componente inteiro.
 * - `modules/returns/collection-step.tsx` — o estado `hasDeclared`, o
 *   `canAdvance` e as duas utilizações do componente.
 * - `locales/*​/private-area.json` — `returns.collection.terms-declaration`.
 */
// Tipada como `boolean` de propósito: sem isso o TypeScript reduz a constante
// ao literal e trata os ramos guardados como código morto.
export const IS_RETURN_TERMS_ENABLED: boolean = true;

/**
 * Apagar os dados de envio que o cliente deu — a transportadora e o número de
 * seguimento de um volume do método `customer`.
 *
 * **Desligada porque a API não tem como o fazer.** Confirmado na documentação
 * viva (`dev.b-parts.com/en/shop/docs/`): a única escrita sobre um volume é o
 * `POST /returns/{id}/shipments/{shipment_id}/tracking`, que **põe** a
 * transportadora e o número; não há `DELETE`, e um `tracking_number` vazio é
 * recusado com `["invalid"]` por ter de ter 4 a 64 caracteres. Apagar implica
 * ainda pôr o grupo de volta em `awaiting_shipment`, e a primeira submissão é
 * documentada como irreversível: o que ela muda de estado, nada desfaz.
 *
 * O caminho do lado do cliente está todo feito e aponta para o `DELETE` que o
 * backend teria de expor. **Para a ligar**: confirmar o endpoint com o backend
 * e pôr isto a `true`.
 *
 * Se não seguir, apagar o que a flag toca — está marcado com `[apagar envio]`:
 *
 * - `services/returns/index.ts` — `deleteReturnTracking`.
 * - `modules/returns/tracking-form.tsx` — a prop `onDelete`, o estado
 *   `isConfirmingDelete` e o bloco dos comandos do resumo.
 * - `modules/returns/group-card.tsx`, `parts-summary.tsx`, `summary-step.tsx` —
 *   a prop `onDeleteTracking`.
 * - `pages/private-area/returns/index.tsx` — o `removeTracking`.
 * - `locales/*​/private-area.json` — `returns.tracking.remove`,
 *   `remove-label` e `remove-confirm`.
 */
// Tipada como `boolean` de propósito: sem isso o TypeScript reduz a constante
// ao literal `false` e trata os ramos guardados como código morto.
export const IS_RETURN_TRACKING_DELETE_ENABLED: boolean = false;
